import React, { forwardRef, Fragment, useCallback } from "react";
import classNames from "classnames/dedupe";
import {
  IArticleWidgetConfig,
  IArticleWidgetProps,
  IArticleWidgetTypeConfig,
  TArticleWidgetSizeConfig,
} from "./ArticleWidget.interface";
import deepClone from "../../../helpers/deepClone";
import mergeDeepWithDefaults from "../../../helpers/mergeDeepWithDefaults";
import { Typography } from "../../Fundamentals/Typography";
import WidgetMedia from "../WidgetMedia/WidgetMedia";
import { SvgIcon } from "../../Icons";
import { CustomImageWrapper } from "../../Utility/CustomImageWrapper";

const PrimaryTypeBase: IArticleWidgetTypeConfig = {
  root: "widget widget-article widget-article__blue",
  shader: "uds-shader uds-shader__blue d-none d-md-block",
};

const SecondaryTypeBase: IArticleWidgetTypeConfig = {
  root: "widget widget-article widget-article__red",
  shader: "uds-shader uds-shader__red d-none d-md-block",
};

const sharedWrapper =
  "widget--info d-flex flex-column justify-content-center p-6 p-lg-7";

const largeArticleWidgetConfig: TArticleWidgetSizeConfig = {
  default: {
    ratio: "16x9",
    root: "widget widget-article widget-article__large",
    wrapper: "widget--info d-flex flex-column justify-content-center",
    shader: "uds-shader uds-shader__faded d-none d-md-block",
    tagsProps: {
      as: "h4",
      variant: "h-card",
      classes: { root: "mb-4 mb-md-5 max-lines max-lines--1 word-break-word" },
    },
    titleProps: {
      as: "h2",
      variant: "h2-widget",
      classes: { root: " mb-6 word-break-word max-lines max-lines--2" },
    },
    descriptionProps: {
      as: "p",
      variant: "p-medium",
      classes: {
        root: "d-none d-sm--block d-sm--webkit-box mb-6 mb-lg-7 word-break-word max-lines max-lines--2",
      },
    },
  },
  primary: PrimaryTypeBase,
  secondary: SecondaryTypeBase,
};

const doubleArticleWidgetConfig: TArticleWidgetSizeConfig = {
  default: {
    ratio: "min-150", // at least 150px height (min-height)
    root: "widget widget-article widget-article__double flex-grow-1",
    wrapper: "widget--info d-flex flex-column justify-content-center",
    shader: "uds-shader uds-shader__faded",
    tagsProps: {
      as: "h4",
      variant: "h-card",
      classes: {
        root: "h-card--tablet mb-4 mb-md-4 mb-lg-5 word-break-word max-lines max-lines--1",
      },
    },
    titleProps: {
      as: "h2",
      variant: "h3-pdp",
      classes: { root: "mb-4 mb-md-6 word-break-word max-lines max-lines--2" },
    },
    descriptionProps: {
      classes: {
        root: "word-break-word max-lines max-lines--2",
      },
    },
  },
  primary: {
    shader: "uds-shader uds-shader__blue",
  },
  secondary: {
    shader: "uds-shader uds-shader__red",
  },
};

const mediumArticleWidgetConfig: TArticleWidgetSizeConfig = {
  ...largeArticleWidgetConfig,
  default: {
    ...largeArticleWidgetConfig.default,
    ratio: "5x4",
    root: "widget widget-article widget-article__medium",
    wrapper:
      "widget--info d-flex flex-column justify-content-center p-6 p-lg-7",
    shader: "d-none",
    tagsProps: {
      as: "h4",
      variant: "h-card",
      classes: {
        root: "mb-5 d-none d-md-block d-md--webkit-box word-break-word max-lines max-lines--1",
      },
    },
    titleProps: {
      as: "h2",
      variant: "h2-enriched",
      classes: {
        root: "mb-7 mb-lg-10 word-break-word max-lines max-lines--2",
      },
    },
  },
  primary: {
    wrapper: `${sharedWrapper} bg-color-blue text-color-white`,
  },
  secondary: {
    wrapper: `${sharedWrapper} bg-color-red text-color-white`,
  },
};

const smallArticleWidgetConfig: TArticleWidgetSizeConfig = {
  default: {
    ratio: "5x4",
    root: "widget widget-article widget-article__small",
    wrapper: sharedWrapper,
    tagsProps: {
      as: "h4",
      variant: "h-card",
      classes: {
        root: "h-card mb-4 d-none d-md-block d-md--webkit-box word-break-word max-lines max-lines--1",
      },
    },
    titleProps: {
      as: "h3",
      variant: "h3-enriched",
      classes: {
        root: "h3-enriched mb-7 mb-md-6 word-break-word max-lines max-lines--2",
      },
    },
    descriptionProps: {
      classes: {
        root: "word-break-word max-lines max-lines--2",
      },
    },
  },
  primary: {
    wrapper: `${sharedWrapper} bg-color-blue text-color-white`,
  },
  secondary: {
    wrapper: `${sharedWrapper} bg-color-red text-color-white`,
  },
};

const singleArticleWidgetConfig: TArticleWidgetSizeConfig = {
  default: {
    ratio: "5x4",
    root: "widget widget-article widget-article__single",
    wrapper: sharedWrapper,
    tagsProps: {
      as: "h4",
      variant: "h-card",
      classes: {
        root: "h-card mb-4 d-none d-md-block d-md--webkit-box word-break-word max-lines max-lines--1",
      },
    },
    titleProps: {
      as: "h3",
      variant: "h3-enriched",
      classes: {
        root: "h3-enriched mb-7 mb-md-6 word-break-word max-lines max-lines--2",
      },
    },
    descriptionProps: {
      classes: {
        root: "word-break-word max-lines max-lines--2",
      },
    },
  },
  primary: {
    wrapper: `${sharedWrapper} bg-color-blue text-color-white`,
  },
  secondary: {
    wrapper: `${sharedWrapper} bg-color-red text-color-white`,
  },
};

const rawVariants: IArticleWidgetConfig = {
  large: largeArticleWidgetConfig,
  double: doubleArticleWidgetConfig,
  medium: mediumArticleWidgetConfig,
  small: smallArticleWidgetConfig,
  single: singleArticleWidgetConfig,
};

const variants = deepClone(rawVariants);

/** Extends with defaults.
 *  For example, 'primary' variant will have all values of 'default' + own overrides from the raw config
 */
Object.keys(rawVariants).forEach((sizeKey) => {
  variants[sizeKey] = {
    ...mergeDeepWithDefaults(
      rawVariants[sizeKey],
      deepClone(rawVariants[sizeKey].default),
    ),
  };
});

export { variants as ArticleWidgetDefaultVariantsSettings };

const ArticleWidget = forwardRef<HTMLDivElement, IArticleWidgetProps>(
  (
    {
      id,
      size = "large",
      type = "default",
      ratio,
      tags,
      title,
      description,
      commentsCount,
      readInfo,
      mediaData,
      classes,
      variantsConfig,
      children,
      onComment,
    },
    ref,
  ) => {
    const styleSetting = (variantsConfig || variants)[size][type];
    const rootClasses = classNames(classes?.root, styleSetting?.root);
    const wrapperClasses = classNames(
      classes?.wrapper,
      styleSetting?.text,
      styleSetting?.wrapper,
      styleSetting?.background,
    );

    const commentHandler = useCallback(
      (event) => {
        if (!onComment) return;
        event.preventDefault();
        event.stopPropagation();
        onComment(event, { id });
      },
      [id, onComment],
    );
    const curRatio = ratio || styleSetting?.ratio;

    return (
      <div className={rootClasses} ref={ref} data-testid="ArticleWidget">
        {mediaData ? (
          <CustomImageWrapper ratio={curRatio}>
            <WidgetMedia
              classes={classes?.media}
              ratio={curRatio}
              mediaData={mediaData}
            />
          </CustomImageWrapper>
        ) : null}
        <div className={wrapperClasses}>
          {tags?.length ? (
            <Typography {...(styleSetting?.tagsProps ?? {})}>
              {tags.map((tag, index) => (
                <Fragment key={tag}>
                  {index !== 0 && <span>&nbsp;&nbsp;·&nbsp;&nbsp;</span>}
                  <span>{tag}</span>
                </Fragment>
              ))}
            </Typography>
          ) : null}
          {title ? (
            <Typography {...(styleSetting?.titleProps ?? {})}>
              {title}
            </Typography>
          ) : null}
          {description ? (
            <Typography {...(styleSetting?.descriptionProps ?? {})}>
              {description}
            </Typography>
          ) : null}
          <div className="d-flex align-items-center text-small">
            {commentsCount !== undefined ? (
              <div
                className={classNames("align-items-center", {
                  "d-none d-md-flex": ["double", "single"].includes(size),
                  "d-none d-lg-flex": ["small"].includes(size),
                  "d-flex": !["double", "single", "small"].includes(size),
                })}
                onClickCapture={commentHandler}
              >
                <SvgIcon
                  className="icon-container"
                  icon="CommentIcon"
                  data-testid="SvgIcon__CommentIcon"
                />
                <span className="ms-4">{commentsCount}</span>
              </div>
            ) : null}
            {readInfo ? (
              <span
                data-testid="ReadInfo__Text"
                className={classNames("ms-auto white-space-nowrap", {
                  "ms-md-7 ms-lg-8": size === "large",
                  "ms-md-6 ms-lg-7": size === "double",
                })}
              >
                {readInfo} min read
              </span>
            ) : null}
          </div>
        </div>
        <div className={styleSetting?.shader}>
          <div className="overlays">
            <div />
            <div />
            <div />
            <div />
            <div />
          </div>
        </div>
        {children}
      </div>
    );
  },
);

export default ArticleWidget;
